import { useEffect, useState } from "react";
import { ChefHat, Sparkles, ChevronRight, X } from "lucide-react";
import { Button } from "../ui/button";
import api, { logClientEvent } from "../../lib/api";

/**
 * StarterUsageBanner — barra de uso persistente para el plan Starter.
 * Muestra en vivo "X/50 platos" + "Y/30 créditos IA" + CTA "Comprar créditos / Mejorar plan".
 *
 * Auto-hide para planes superiores (Essentials/Premium) o si la suscripción no se ha cargado.
 *
 * Props:
 *  - restaurantId: string (requerido para fetchear créditos del plan)
 *  - dishCount: int (platos del menú actual)
 */
export default function StarterUsageBanner({ restaurantId, dishCount = 0 }) {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("starter_banner_dismissed") === "1") {
      setDismissed(true);
    }
    if (!restaurantId) return;
    api.request(`/subscription/my/${restaurantId}`)
      .then((r) => setSub(r?.subscription || null))
      .catch(() => {});
  }, [restaurantId]);

  if (dismissed) return null;
  if (!sub || sub.plan_id !== "plan_starter") return null;

  const creditsTotal = Number(sub.credits_total || 0);
  const creditsUsed = Number(sub.credits_used || 0);
  const creditsLeft = Math.max(0, creditsTotal - creditsUsed);
  const creditsPct = creditsTotal > 0 ? Math.min(100, (creditsUsed / creditsTotal) * 100) : 0;
  const dishesPct = Math.min(100, (dishCount / 50) * 100);

  // Highlight color logic
  const dishesAlmostFull = dishCount >= 45;
  const creditsAlmostEmpty = creditsLeft <= 5;
  const showAlert = dishesAlmostFull || creditsAlmostEmpty;

  const handleDismiss = () => {
    sessionStorage.setItem("starter_banner_dismissed", "1");
    setDismissed(true);
    // Telemetry: how often Starter users dismiss this banner per session?
    logClientEvent({
      type: "starter_banner_dismissed",
      status: 200,
      endpoint: window.location.pathname,
      message: `dishes=${dishCount}/50 credits=${creditsUsed}/${creditsTotal}`,
      restaurant_id: restaurantId,
    });
  };

  return (
    <div
      className={`relative rounded-2xl border p-4 shadow-sm transition ${
        showAlert
          ? "border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40"
          : "border-violet-200 bg-violet-50 dark:bg-violet-900/10 dark:border-violet-700/40"
      }`}
      data-testid="starter-usage-banner"
    >
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground"
        aria-label="Cerrar"
        data-testid="starter-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Plan badge */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-violet-200 dark:bg-violet-800/50 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-violet-700 dark:text-violet-300" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Plan Starter</p>
            <p className="text-xs text-muted-foreground">Tu uso este mes</p>
          </div>
        </div>

        {/* Dishes usage */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <ChefHat className="w-3 h-3" /> Platos
            </span>
            <span className={`tabular-nums font-bold ${dishesAlmostFull ? "text-amber-700 dark:text-amber-400" : ""}`}>
              {dishCount}<span className="text-muted-foreground font-normal"> / 50</span>
            </span>
          </div>
          <div className="h-1.5 bg-card/70 dark:bg-black/30 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${dishesAlmostFull ? "bg-amber-500" : "bg-violet-500"}`}
              style={{ width: `${dishesPct}%` }}
            />
          </div>
        </div>

        {/* Credits usage */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Sparkles className="w-3 h-3" /> Créditos IA
            </span>
            <span className={`tabular-nums font-bold ${creditsAlmostEmpty ? "text-amber-700 dark:text-amber-400" : ""}`}>
              {creditsUsed}<span className="text-muted-foreground font-normal"> / {creditsTotal}</span>
            </span>
          </div>
          <div className="h-1.5 bg-card/70 dark:bg-black/30 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${creditsAlmostEmpty ? "bg-amber-500" : "bg-violet-500"}`}
              style={{ width: `${creditsPct}%` }}
            />
          </div>
        </div>

        {/* CTAs */}
        <div className="flex gap-2 shrink-0 pr-6">
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-8"
            onClick={() => { window.location.href = "/app/subscription?tab=credits"; }}
            data-testid="starter-banner-buy-credits"
          >
            Comprar créditos
          </Button>
          <Button
            size="sm"
            className="text-xs h-8 bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => { window.location.href = "/app/subscription?tab=plans"; }}
            data-testid="starter-banner-upgrade"
          >
            Mejorar plan <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
